import { useState, useEffect, useCallback, useId, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'sonner'
import {
  X,
  CalendarPlus,
  Clock,
  CircleNotch,
  Plus,
  Check,
  MagnifyingGlass,
} from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Skeleton } from '@/components/ui/Skeleton'
import DateTimePicker from '@/components/ui/DateTimePicker'
import { cn } from '@/lib/utils'
import { nextTabIndex } from '@/lib/rovingTabs'
import outlookLogo from '@/assets/providers/outlook.webp'
import zoomLogo from '@/assets/providers/zoom.png'
import calendlyLogo from '@/assets/providers/calendly.png'
import {
  meetingsApi,
  contactsApi,
  type MeetingHost,
  type CalendlyEventType,
  type MeetingSlot,
  type Meeting,
} from '@/lib/api'

type Provider = 'outlook' | 'zoom' | 'calendly'

interface ContactRef {
  id: number
  name?: string
  email?: string
}

interface BookMeetingModalProps {
  isOpen: boolean
  onClose: () => void
  /** When provided, the meeting is booked for this contact (locked). Omit on the
   * Tasks page to show a contact picker instead. */
  contact?: ContactRef
  /** When provided, the modal reschedules this existing meeting instead of booking
   * a new one: provider/host/contact are locked, only date + slot change. */
  rescheduleTarget?: Meeting
  /** When booking fulfils a workflow "book meeting" task, link the new meeting to it. */
  taskId?: number
  onBooked?: (meeting: Meeting) => void
}

const PROVIDERS: { value: Provider; label: string; logo: string }[] = [
  { value: 'outlook', label: 'Outlook', logo: outlookLogo },
  { value: 'zoom', label: 'Zoom', logo: zoomLogo },
  { value: 'calendly', label: 'Calendly', logo: calendlyLogo },
]

const DURATIONS = [15, 30, 45, 60]

// IANA timezone names — the format Outlook (Graph), Zoom, and Calendly all accept.
// Prefer the browser's full supported (canonical) list; fall back to a common subset.
// The canonical list omits legacy aliases like GMT / Etc/GMT, so we merge those in
// (valid IANA fixed-offset zones) so users can pick/search "GMT" offsets too.
export interface TimezoneOption {
  /** Canonical IANA name, e.g. "Asia/Kolkata" — what the providers accept. */
  value: string
  /** Human form shown in the list, e.g. "Asia Kolkata" or "GMT+05:00". */
  label: string
}

const TIMEZONE_OPTIONS: TimezoneOption[] = (() => {
  let zones: string[] = []
  try {
    const fn = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf
    if (typeof fn === 'function') zones = fn('timeZone')
  } catch { /* not supported */ }
  if (!zones.length) {
    zones = [
      'UTC', 'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Tokyo', 'Asia/Shanghai',
      'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'America/New_York', 'America/Chicago',
      'America/Denver', 'America/Los_Angeles', 'America/Sao_Paulo', 'Australia/Sydney',
    ]
  }
  const base = zones.map(z => ({ value: z, label: z.replace(/_/g, ' ') }))

  // GMT / UTC fixed-offset zones. Note IANA's Etc/GMT sign is inverted
  // (Etc/GMT+5 == UTC-05:00), so the labels below show the TRUE UTC offset.
  const gmt: TimezoneOption[] = [{ value: 'Etc/GMT', label: 'GMT / UTC+00:00' }]
  for (let h = 1; h <= 14; h++) {
    const hh = String(h).padStart(2, '0')
    gmt.push({ value: `Etc/GMT-${h}`, label: `GMT+${hh}:00` }) // Etc/GMT-h == UTC+h
    if (h <= 12) gmt.push({ value: `Etc/GMT+${h}`, label: `GMT-${hh}:00` }) // Etc/GMT+h == UTC-h
  }

  const seen = new Set(base.map(o => o.value))
  return [...base, ...gmt.filter(o => !seen.has(o.value))]
})()

/** Rows the popup will render at most. The full list is ~400 zones, and putting
 *  all of them in a 224px scroller is DOM nobody scrolls to — typing narrows
 *  faster than scrolling does. */
export const TIMEZONE_MATCH_LIMIT = 60

/**
 * Type-to-filter matching for the timezone combobox.
 *
 * Matches the human label ("Asia Kolkata", "GMT+05:00") *and* the canonical
 * IANA value ("Asia/Kolkata"), because users type either form — the underscore
 * in the canonical name is exactly what the label strips, so searching one
 * without the other would miss half the ways people spell a zone. An empty
 * query returns the head of the list rather than nothing, so opening the popup
 * always shows something to arrow through.
 */
export function filterTimezones(
  query: string,
  options: readonly TimezoneOption[] = TIMEZONE_OPTIONS,
  limit: number = TIMEZONE_MATCH_LIMIT,
): TimezoneOption[] {
  const q = query.trim().toLowerCase()
  const matched = q
    ? options.filter(o => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q))
    : options
  return matched.slice(0, limit)
}

/**
 * Which option the given key should make active, or `null` when the key is not
 * part of the listbox key set (so the caller leaves the event alone).
 *
 * Delegates to the roving-tablist helper: a vertical listbox and a vertical
 * tablist need the identical movement rules — wrap at both ends, Home/End to
 * the extremes, and a stale/out-of-range current index treated as 0 — and one
 * tested implementation beats two that drift apart. Wrapping (rather than
 * clamping at the ends) is the right call for a list capped at
 * TIMEZONE_MATCH_LIMIT rows in a ~7-row viewport: it costs one keypress to get
 * from the last match back to the first instead of 59.
 */
export function nextActiveIndex(key: string, current: number, count: number): number | null {
  return nextTabIndex(key, current, count, 'vertical')
}

const browserTz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

const initials = (name?: string) => {
  const s = (name || '').trim()
  if (!s) return '?'
  const parts = s.split(/\s+/)
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase()
}

// Format a UTC ISO instant as a time in the chosen IANA timezone (e.g. show a
// Calendly/Outlook slot's 03:30Z as "09:00 AM" for an Asia/Kolkata user). Falls
// back to the raw label if the timezone is invalid.
const formatSlotTime = (iso: string, tz: string) => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: '2-digit', minute: '2-digit', timeZone: tz || 'UTC',
    }).format(new Date(iso))
  } catch {
    return new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(new Date(iso))
  }
}

const todayStr = () => {
  const d = new Date()
  const off = d.getTimezoneOffset()
  const local = new Date(d.getTime() - off * 60000)
  return local.toISOString().slice(0, 10)
}

// YYYY-MM-DD of an ISO instant in the given IANA timezone ('en-CA' formats as
// YYYY-MM-DD). Used to open the reschedule dialog on the meeting's current day.
const dateInTz = (iso: string, tz?: string) => {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric', month: '2-digit', day: '2-digit', timeZone: tz || undefined,
    }).format(new Date(iso))
  } catch {
    return todayStr()
  }
}

// Suggestive (type-to-filter) timezone picker. The list is long (~400 IANA
// zones), so free-typing filters as you go; selecting stores the canonical value.
function TimezoneCombobox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const ref = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  // Hover syncs the active row to the pointer (so Enter and a click can never
  // disagree about which zone is next), but hover must NOT scroll: nudging a
  // half-visible row into view slides the following row under a stationary
  // cursor, which fires another mouseenter and walks the list on its own. So
  // only keyboard movement and typing arm the scroll.
  const scrollToActive = useRef(false)

  // useId, not a module-level counter or a hard-coded string: the Tasks page
  // mounts this modal three times at once (book / book-for-task / reschedule),
  // so fixed ids would collide and every instance's aria-activedescendant would
  // resolve to the first instance's rows.
  const uid = useId()
  const listboxId = `${uid}tz-listbox`
  const optionId = (index: number) => `${uid}tz-opt-${index}`

  const matches = useMemo(() => filterTimezones(query), [query])

  // Read through this, never through activeIndex directly: the filtered list can
  // shrink in the same tick the index was set, so clamping at the point of use
  // makes an out-of-range active row structurally impossible. -1 == nothing
  // active, which is the honest state for an empty result.
  const active = matches.length > 0 ? Math.min(activeIndex, matches.length - 1) : -1

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('mousedown', handler)
    return () => window.removeEventListener('mousedown', handler)
  }, [open])

  // Narrowing the query should restart at the best (first) match rather than
  // leave the highlight parked wherever the previous, longer list had it.
  useEffect(() => { setActiveIndex(0) }, [query])

  // aria-activedescendant moves *visual* focus without moving DOM focus, so the
  // browser does no scrolling of its own — the popup must chase the active row
  // itself or arrowing past the 7th match highlights something off-screen.
  // Located by DOM order rather than by id: React's useId contains colons,
  // which are not valid in a querySelector id selector.
  useEffect(() => {
    if (!open || active < 0 || !scrollToActive.current) return
    listRef.current?.querySelectorAll<HTMLElement>('[role="option"]')[active]
      ?.scrollIntoView({ block: 'nearest' })
  }, [open, active, matches])

  const commit = (option: TimezoneOption) => {
    onChange(option.value)
    setOpen(false)
    setQuery('')
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      // Only swallow Escape while the popup is open — closed, it has to reach
      // the dialog so Escape still dismisses the modal.
      if (!open) return
      e.preventDefault()
      e.stopPropagation()
      setOpen(false)
      setQuery('') // drops back to the value.replace() display, committing nothing
      return
    }
    // Deliberately no preventDefault: Tab should close the popup and then move
    // on to the next field the way it normally would.
    if (e.key === 'Tab') {
      setOpen(false)
      return
    }
    if (e.key === 'Enter') {
      if (!open) return
      e.preventDefault() // an open popup consumes Enter; it must never submit
      if (active >= 0) commit(matches[active])
      return
    }
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault()
      scrollToActive.current = true
      setActiveIndex(0)
      setOpen(true)
      return
    }
    const next = nextActiveIndex(e.key, active, matches.length)
    if (next === null) return
    e.preventDefault() // stop the arrows moving the caret / Home-End jumping in the text
    scrollToActive.current = true
    setActiveIndex(next)
  }

  return (
    <div ref={ref} className="relative">
      <input
        type="text"
        role="combobox"
        aria-label="Timezone"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
        aria-autocomplete="list"
        // The browser's own suggestion popup would sit on top of the listbox.
        autoComplete="off"
        value={open ? query : value.replace(/_/g, ' ')}
        // The <ul> node survives a query change and keeps its scrollTop, so a
        // narrowed list must scroll back to the row the reset re-activates.
        onChange={e => { scrollToActive.current = true; setQuery(e.target.value); if (!open) setOpen(true) }}
        onFocus={() => { setQuery(''); setOpen(true) }}
        // Selecting a row keeps focus in the input on purpose (see the row's
        // onMouseDown), so a second click fires no focus event — without this,
        // clicking the input after picking a zone would not reopen the popup.
        onClick={() => { if (!open) setOpen(true) }}
        onKeyDown={onKeyDown}
        placeholder="Search timezone..."
        className="w-full h-10 rounded-md px-3 text-sm border border-border/60 bg-background text-foreground focus:outline-none focus-visible:border-[#7170ff]/55 dark:bg-white/[0.02] dark:border-white/[0.08]"
      />
      {/* No onBlur close: the popup is dismissed by outside-mousedown, Escape and
          Tab, all of which fire before/instead of a blur that would tear the list
          down mid-click and swallow the row's onClick. */}
      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          className="absolute z-[60] mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-border/60 bg-card shadow-lg dark:bg-[#191a1b] dark:border-white/[0.08]">
          {matches.length === 0 ? (
            // Hidden from assistive tech because the status line below announces
            // the same words; showing it in both places reads it out twice.
            <li role="presentation" aria-hidden className="px-3 py-2 text-sm text-muted-foreground">
              No matching timezone
            </li>
          ) : (
            matches.map((o, i) => (
              // One node, one role: the <li> IS the option, with no nested
              // <button>. A focusable child inside role="option" both breaks the
              // listbox's structure and made all 60 rows separate tab stops.
              <li
                key={o.value}
                id={optionId(i)}
                role="option"
                aria-selected={o.value === value}
                // Commit on click, so the pointer and the input's Enter key run
                // the one code path. onMouseDown only cancels the default focus
                // shift, keeping focus in the input so the click still lands.
                onMouseDown={e => e.preventDefault()}
                onClick={() => commit(o)}
                onMouseEnter={() => { scrollToActive.current = false; setActiveIndex(i) }}
                className={cn(
                  'cursor-pointer px-3 py-1.5 text-sm transition-colors',
                  // Keyboard-active is its own state, stronger than hover and
                  // distinct from the indigo "this is the current value" text.
                  i === active ? 'bg-muted dark:bg-white/[0.06]' : 'hover:bg-muted/50 dark:hover:bg-white/[0.04]',
                  o.value === value && 'text-[#5e6ad2] dark:text-[#a5b4fc] font-medium',
                )}>
                {o.label}
              </li>
            ))
          )}
        </ul>
      )}
      {/* Lives outside the `open &&` popup on purpose: a live region only
          reliably announces changes to a node that was already in the DOM, so
          annotating the "No matching timezone" row itself would announce
          nothing the moment it is inserted. */}
      <p role="status" aria-live="polite" className="sr-only">
        {open && matches.length === 0 ? 'No matching timezone' : ''}
      </p>
    </div>
  )
}

export default function BookMeetingModal({ isOpen, onClose, contact, rescheduleTarget, taskId, onBooked }: BookMeetingModalProps) {
  const isReschedule = !!rescheduleTarget
  const [provider, setProvider] = useState<Provider>('outlook')
  const [hosts, setHosts] = useState<MeetingHost[]>([])
  const [hostsLoading, setHostsLoading] = useState(true)
  const [eventTypes, setEventTypes] = useState<CalendlyEventType[]>([])
  const [outlookHost, setOutlookHost] = useState('')
  const [zoomHost, setZoomHost] = useState('')
  const [host, setHost] = useState('')
  const [duration, setDuration] = useState(30)
  const [timezone, setTimezone] = useState(browserTz())
  const [date, setDate] = useState(todayStr())
  const [slots, setSlots] = useState<MeetingSlot[]>([])
  const [slotsLoading, setSlotsLoading] = useState(false)
  const [slotsError, setSlotsError] = useState<string | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<MeetingSlot | null>(null)
  const [attendees, setAttendees] = useState<string[]>([])
  const [attendeeInput, setAttendeeInput] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [booking, setBooking] = useState(false)
  const [booked, setBooked] = useState<Meeting | null>(null)

  // Contact picker (Tasks-page mode, when no contact prop)
  const [allContacts, setAllContacts] = useState<ContactRef[]>([])
  const [contactSearch, setContactSearch] = useState('')
  const [contactsLoading, setContactsLoading] = useState(false)
  const [contactsLoadingMore, setContactsLoadingMore] = useState(false)
  const [contactPage, setContactPage] = useState(0)
  const [contactHasMore, setContactHasMore] = useState(false)
  const [pickedContact, setPickedContact] = useState<ContactRef | null>(null)

  const CONTACT_PAGE_SIZE = 15
  const fetchingRef = useRef(false)

  const activeContact = contact ?? (isReschedule ? { id: rescheduleTarget!.contact_id } : pickedContact)

  // Reset when opened
  useEffect(() => {
    if (!isOpen) return
    setSlots([])
    setSelectedSlot(null)
    setAttendeeInput('')
    setBooked(null)
    setHostsLoading(true)
    setPickedContact(null)
    setContactSearch('')
    setContactPage(0)
    if (rescheduleTarget) {
      setProvider(rescheduleTarget.provider as Provider)
      setHost(rescheduleTarget.host_email)
      setDuration(rescheduleTarget.duration_minutes)
      setTimezone(rescheduleTarget.timezone || browserTz())
      // Open on the meeting's currently scheduled day (not today), so the dialog
      // reflects the latest booked date/time after prior reschedules.
      setDate(
        rescheduleTarget.start_time
          ? dateInTz(rescheduleTarget.start_time, rescheduleTarget.timezone)
          : todayStr(),
      )
      setAttendees(rescheduleTarget.attendees || [])
      setDescription(rescheduleTarget.description || '')
      setTitle(rescheduleTarget.title)
    } else {
      setProvider('outlook')
      setHost('')
      setDuration(30)
      setTimezone(browserTz())
      setDate(todayStr())
      setAttendees([])
      setDescription('')
      setTitle(contact?.name ? `Meeting with ${contact.name}` : '')
    }
    // Depend on stable identity primitives, NOT the `contact` / `rescheduleTarget`
    // object references. Parents (e.g. ContactDetailModal) pass a fresh object
    // literal on every render, so keying on the objects re-fired this reset on
    // unrelated re-renders (like the Contacts page's window-focus refresh),
    // flipping hostsLoading back to true while the loader effect below (keyed on
    // isOpen) never re-ran, leaving the modal stuck on its skeleton (blank).
  }, [isOpen, contact?.id, rescheduleTarget?.id])

  // Load hosts + Calendly event types on open
  useEffect(() => {
    if (!isOpen) return
    setHostsLoading(true)
    meetingsApi.getHosts()
      .then(res => {
        setHosts(res.data.hosts)
        setEventTypes(res.data.calendly_event_types)
        setOutlookHost(res.data.outlook_host || '')
        setZoomHost(res.data.zoom_host || '')
        // Default provider is Outlook; host is that integration account.
        if (!isReschedule && provider !== 'calendly') {
          setHost((provider === 'zoom' ? res.data.zoom_host : res.data.outlook_host) || '')
        }
      })
      .catch(() => toast.error('Failed to load meeting hosts'))
      .finally(() => setHostsLoading(false))
  }, [isOpen])

  // Load one page of contacts (server-side search + skip/limit). replace=true
  // starts a fresh list (new search / reopen); replace=false appends (scroll).
  const loadContacts = useCallback(async (page: number, replace: boolean) => {
    if (fetchingRef.current) return
    fetchingRef.current = true
    replace ? setContactsLoading(true) : setContactsLoadingMore(true)
    try {
      const res = await contactsApi.getAll({
        search: contactSearch.trim() || undefined,
        limit: CONTACT_PAGE_SIZE,
        skip: page * CONTACT_PAGE_SIZE,
      })
      const list: ContactRef[] = (res.data || []).map((c: any) => ({
        id: c.id,
        name: c.full_name || [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email,
        email: c.email,
      }))
      setAllContacts(prev => (replace ? list : [...prev, ...list]))
      setContactHasMore(list.length === CONTACT_PAGE_SIZE)
      setContactPage(page)
    } catch {
      /* non-fatal */
    } finally {
      replace ? setContactsLoading(false) : setContactsLoadingMore(false)
      fetchingRef.current = false
    }
  }, [contactSearch])

  // Debounced reset-and-load-first-page whenever the search term (or open) changes.
  useEffect(() => {
    if (!isOpen || contact || pickedContact) return
    const handle = setTimeout(() => loadContacts(0, true), 250)
    return () => clearTimeout(handle)
  }, [isOpen, contact, pickedContact, contactSearch, loadContacts])

  // Infinite scroll: load the next page as the list nears its bottom.
  const onContactsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    if (
      el.scrollHeight - el.scrollTop - el.clientHeight < 64 &&
      contactHasMore && !contactsLoading && !contactsLoadingMore
    ) {
      loadContacts(contactPage + 1, false)
    }
  }

  // When switching to Calendly, host list becomes event types; reset host
  useEffect(() => {
    if (isReschedule) return
    if (provider === 'calendly') {
      setHost(eventTypes[0]?.uri || '')
    } else {
      setHost((provider === 'zoom' ? zoomHost : outlookHost) || '')
    }
    setSelectedSlot(null)
  }, [provider, eventTypes, outlookHost, zoomHost])

  // Calendly event type controls duration
  useEffect(() => {
    if (isReschedule) return
    if (provider === 'calendly' && host) {
      const et = eventTypes.find(e => e.uri === host)
      if (et?.duration) setDuration(et.duration)
    }
  }, [provider, host, eventTypes])

  const fetchSlots = useCallback(() => {
    if (!isOpen || !host) return
    // Guard against a stale host from the previous provider during a tab switch:
    // Calendly needs an event-type URI (https://...), Outlook/Zoom need an email.
    // Without this, switching providers briefly fires a request with the old
    // provider's host (e.g. an email sent as a Calendly event_type -> 400).
    const hostIsUri = /^https?:\/\//.test(host)
    if (provider === 'calendly' ? !hostIsUri : hostIsUri) return
    const dayStart = new Date(`${date}T00:00:00`)
    const dayEnd = new Date(`${date}T23:59:59`)
    setSlotsLoading(true)
    setSlotsError(null)
    setSelectedSlot(null)
    meetingsApi.getAvailability({
      provider,
      host,
      date_from: dayStart.toISOString(),
      date_to: dayEnd.toISOString(),
      duration,
      timezone,
      // Intersect the host's free/busy with attendees' (Outlook/Zoom only; ignored
      // for Calendly). Only tenant mailboxes constrain the result.
      attendees: provider === 'calendly' ? undefined : attendees.join(','),
    })
      .then(res => setSlots(res.data))
      .catch(err => {
        setSlots([])
        setSlotsError(err?.response?.data?.detail || 'Failed to load availability')
      })
      .finally(() => setSlotsLoading(false))
  }, [isOpen, host, provider, date, duration, timezone, attendees])

  // Debounced so quickly changing host/date/duration/attendees/timezone doesn't
  // fire a burst of availability calls (Calendly rate-limits aggressively).
  useEffect(() => {
    const h = setTimeout(() => fetchSlots(), 450)
    return () => clearTimeout(h)
  }, [fetchSlots])

  const addAttendee = () => {
    const v = attendeeInput.trim()
    if (v && /\S+@\S+\.\S+/.test(v) && !attendees.includes(v)) {
      setAttendees(a => [...a, v])
    }
    setAttendeeInput('')
  }

  const selectedEventType = useMemo(
    () => eventTypes.find(e => e.uri === host),
    [eventTypes, host],
  )

  const canBook = !!activeContact && !!host && !!selectedSlot && !booking

  const handleBook = async () => {
    if (!activeContact || !selectedSlot) return
    setBooking(true)
    try {
      if (isReschedule && rescheduleTarget) {
        const res = await meetingsApi.updateMeeting(rescheduleTarget.id, {
          start_time: selectedSlot.start,
          duration,
          title: title || undefined,
          description: description || undefined,
          attendees,
        })
        setBooked(res.data)
        toast.success('Meeting rescheduled')
        onBooked?.(res.data)
      } else {
        const res = await meetingsApi.createMeeting({
          contact_id: activeContact.id,
          provider,
          host,
          title: title || undefined,
          description: description || undefined,
          start_time: selectedSlot.start,
          duration,
          attendees,
          timezone,
          task_id: taskId,
        })
        setBooked(res.data)
        toast.success('Meeting booked')
        onBooked?.(res.data)
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || (isReschedule ? 'Failed to reschedule' : 'Failed to book meeting'))
    } finally {
      setBooking(false)
    }
  }

  const handleSendCalendlyLink = async () => {
    if (!activeContact) return
    const url = selectedEventType?.scheduling_url || selectedSlot?.scheduling_url
    if (!url) {
      toast.error('No Calendly scheduling link available for this event type')
      return
    }
    setBooking(true)
    try {
      await meetingsApi.sendCalendlyLink({ contact_id: activeContact.id, scheduling_url: url })
      toast.success('Scheduling link sent to the contact')
      onClose()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to send link')
    } finally {
      setBooking(false)
    }
  }

  if (!isOpen) return null

  const hostOptions = provider === 'calendly'
    ? eventTypes.map(e => ({ value: e.uri || '', label: `${e.name || 'Event'}${e.duration ? ` (${e.duration}m)` : ''}` }))
    : hosts.map(h => ({ value: h.email, label: h.name }))

  return createPortal(
    <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-[100]">
      <div className={cn(
        'bg-card border border-border/60 rounded-xl shadow-lg w-full max-h-[90vh] flex flex-col mx-4',
        'transition-[max-width] duration-300 ease-out',
        hostsLoading ? 'max-w-md' : 'max-w-xl',
      )}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/60">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-[#5e6ad2]/10 flex items-center justify-center">
              <CalendarPlus className="w-5 h-5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            </div>
            <div>
              <h3 className="text-base font-emphasis text-foreground">{isReschedule ? 'Reschedule meeting' : 'Book a meeting'}</h3>
              {activeContact?.name && (
                <p className="text-xs text-muted-foreground">with {activeContact.name}</p>
              )}
              {isReschedule && rescheduleTarget?.start_time && (
                <p className="text-xs text-muted-foreground">
                  Currently scheduled:{' '}
                  {new Intl.DateTimeFormat(undefined, {
                    dateStyle: 'medium', timeStyle: 'short',
                    timeZone: rescheduleTarget.timezone || undefined,
                  }).format(new Date(rescheduleTarget.start_time))}
                  {rescheduleTarget.timezone ? ` (${rescheduleTarget.timezone})` : ''}
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto space-y-4">
          {booked ? (
            <div className="text-center py-6 space-y-3">
              <div className="mx-auto h-12 w-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <Check className="w-6 h-6 text-emerald-600 dark:text-emerald-400" weight="bold" />
              </div>
              <p className="text-sm font-medium text-foreground">Meeting booked</p>
              <p className="text-xs text-muted-foreground">{booked.title}</p>
              {booked.join_url && (
                <a href={booked.join_url} target="_blank" rel="noreferrer"
                   className="inline-block text-sm text-[#5e6ad2] dark:text-[#a5b4fc] underline underline-offset-4">
                  Open meeting link
                </a>
              )}
              <div className="pt-2">
                <Button variant="outline" onClick={onClose}>Done</Button>
              </div>
            </div>
          ) : hostsLoading ? (
            <div className="space-y-4 animate-fade-in" aria-busy="true">
              <div><Skeleton variant="text" width="70px" className="mb-2" /><Skeleton height="40px" /></div>
              <div><Skeleton variant="text" width="60px" className="mb-2" /><Skeleton height="40px" /></div>
              <div>
                <Skeleton variant="text" width="72px" className="mb-2" />
                <div className="flex gap-2">
                  {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} width="52px" height="32px" />)}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Skeleton variant="text" width="40px" className="mb-2" /><Skeleton height="40px" /></div>
                <div><Skeleton variant="text" width="70px" className="mb-2" /><Skeleton height="40px" /></div>
              </div>
              <div>
                <Skeleton variant="text" width="110px" className="mb-2" />
                <div className="grid grid-cols-3 gap-1.5">
                  {Array.from({ length: 9 }).map((_, i) => <Skeleton key={i} height="30px" />)}
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Contact picker (Tasks-page mode) */}
              {!contact && !isReschedule && (
                <div>
                  <label className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">Contact</label>
                  {pickedContact ? (
                    <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5">
                      <span className="h-8 w-8 rounded-full bg-[#5e6ad2]/12 text-[#5e6ad2] dark:text-[#a5b4fc] flex items-center justify-center text-xs font-emphasis flex-shrink-0">
                        {initials(pickedContact.name)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground truncate">{pickedContact.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{pickedContact.email}</p>
                      </div>
                      <button
                        className="text-xs font-medium text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline flex-shrink-0"
                        onClick={() => setPickedContact(null)}>
                        Change
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Input
                        icon={<MagnifyingGlass className="w-4 h-4" />}
                        placeholder="Search all contacts by name, email, or company..."
                        value={contactSearch}
                        onChange={e => setContactSearch(e.target.value)}
                      />
                      <div
                        onScroll={onContactsScroll}
                        className="mt-1.5 rounded-lg border border-border/60 max-h-56 overflow-y-auto">
                        {contactsLoading ? (
                          <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
                            <CircleNotch className="w-4 h-4 animate-spin" /> Searching...
                          </div>
                        ) : allContacts.length === 0 ? (
                          <p className="px-3 py-4 text-sm text-muted-foreground">
                            {contactSearch.trim() ? 'No contacts match your search.' : 'No contacts found.'}
                          </p>
                        ) : (
                          <ul className="divide-y divide-border/40">
                            {allContacts.map(c => (
                              <li key={c.id}>
                                <button
                                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted/50 transition-colors"
                                  onClick={() => { setPickedContact(c); if (!title) setTitle(`Meeting with ${c.name}`) }}>
                                  <span className="h-7 w-7 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-[11px] font-emphasis flex-shrink-0">
                                    {initials(c.name)}
                                  </span>
                                  <span className="min-w-0 flex-1">
                                    <span className="block text-sm text-foreground truncate">{c.name}</span>
                                    <span className="block text-xs text-muted-foreground truncate">{c.email}</span>
                                  </span>
                                </button>
                              </li>
                            ))}
                            {contactsLoadingMore && (
                              <li className="flex items-center justify-center gap-2 px-3 py-2.5 text-xs text-muted-foreground">
                                <CircleNotch className="w-3.5 h-3.5 animate-spin" /> Loading more...
                              </li>
                            )}
                            {!contactHasMore && !contactsLoadingMore && (
                              <li className="px-3 py-2 text-center text-[11px] text-muted-foreground/60">
                                End of list
                              </li>
                            )}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Provider */}
              {!isReschedule && (
                <div>
                  <label className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">Provider</label>
                  <div className="grid grid-cols-3 gap-1 rounded-lg border border-border/60 dark:border-white/[0.08] p-1 bg-muted/30 dark:bg-white/[0.02]">
                    {PROVIDERS.map(p => {
                      const active = provider === p.value
                      return (
                        <button key={p.value}
                          onClick={() => setProvider(p.value)}
                          className={cn(
                            'flex items-center justify-center gap-2 h-9 text-sm rounded-md transition-all duration-150',
                            active
                              ? 'bg-card text-foreground shadow-sm font-medium ring-1 ring-[#5e6ad2]/30'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50 dark:hover:bg-white/[0.03]',
                          )}>
                          <span className="h-5 w-5 rounded-[5px] flex items-center justify-center overflow-hidden bg-white border border-border/40 dark:border-white/[0.08] flex-shrink-0 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                            <img
                              src={p.logo}
                              alt=""
                              aria-hidden
                              className={cn('h-4 w-4 object-contain transition-opacity duration-150', !active && 'opacity-60')}
                            />
                          </span>
                          {p.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Host: Calendly picks an event type; Outlook/Zoom is always the
                  account that set up the meeting (read-only). */}
              <div>
                <label className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  {provider === 'calendly' ? 'Calendly event type' : 'Host'}
                </label>
                {provider === 'calendly' && !isReschedule ? (
                  <Select
                    value={host}
                    onChange={setHost}
                    options={hostOptions}
                    placeholder="Select an event type"
                    className="w-full"
                    buttonClassName="w-full"
                  />
                ) : host ? (
                  <div className="flex items-center h-10 rounded-md border border-border/60 dark:border-white/[0.08] px-3 text-sm text-foreground bg-muted/20 dark:bg-white/[0.02]">
                    {host}
                  </div>
                ) : (
                  <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
                    {provider === 'zoom'
                      ? 'No Zoom account connected yet. Add your Zoom credentials in Settings → Integrations, then reopen this dialog.'
                      : 'No meeting host mailbox is set. Add one in Settings → Integrations → Outlook Calendar (“Meeting host mailbox”), then reopen this dialog.'}
                  </div>
                )}
                {provider !== 'calendly' && !isReschedule && host && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {provider === 'zoom'
                      ? 'Hosted by the Zoom account that owns your API credentials.'
                      : 'Hosted on this Microsoft mailbox’s calendar; invites are sent from it.'}
                  </p>
                )}
              </div>

              {isReschedule && provider === 'calendly' && (
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  Calendly meetings can't be rescheduled here. Cancel and rebook instead.
                </p>
              )}

              {/* Duration */}
              <div>
                <label className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">Duration</label>
                <div className="flex gap-2 flex-wrap">
                  {DURATIONS.map(d => (
                    <button key={d}
                      disabled={provider === 'calendly'}
                      onClick={() => setDuration(d)}
                      className={cn(
                        'inline-flex items-center justify-center h-9 min-w-[52px] px-3 text-sm rounded-md border transition-colors duration-150',
                        duration === d
                          ? 'border-[#5e6ad2] bg-[#5e6ad2]/10 text-[#5e6ad2] font-medium dark:border-[#5e6ad2]/60 dark:bg-[#5e6ad2]/[0.15] dark:text-[#a5b4fc]'
                          : 'border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:border-white/[0.08] dark:hover:bg-white/[0.03]',
                        provider === 'calendly' && 'opacity-50 cursor-not-allowed hover:bg-transparent dark:hover:bg-transparent',
                      )}>
                      {d}m
                    </button>
                  ))}
                </div>
                {provider === 'calendly' && (
                  <p className="mt-1 text-xs text-muted-foreground">Duration is set by the Calendly event type.</p>
                )}
              </div>

              {/* Date + timezone */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">Date</label>
                  <DateTimePicker mode="date" value={date} onChange={setDate} ariaLabel="Meeting date" />
                </div>
                <div>
                  <label className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">Timezone</label>
                  <TimezoneCombobox value={timezone} onChange={setTimezone} />
                </div>
              </div>

              {/* Attendees (affect availability, so chosen before slots) */}
              <div>
                <label className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">Attendees</label>
                {attendees.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {attendees.map(a => (
                      <span key={a} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-muted/60 text-xs text-foreground">
                        {a}
                        <button onClick={() => setAttendees(list => list.filter(x => x !== a))}>
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Input value={attendeeInput}
                    onChange={e => setAttendeeInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAttendee() } }}
                    placeholder="attendee@email.com" />
                  <Button variant="outline" size="icon" className="h-10 w-10 flex-shrink-0" onClick={addAttendee}><Plus className="w-4 h-4" /></Button>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  The contact is invited automatically. {provider !== 'calendly' && 'Slots below are filtered to times all attendees in your organization are free.'}
                </p>
              </div>

              {/* Slots */}
              <div>
                <label className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> Available slots
                  <span className="normal-case tracking-normal text-muted-foreground/70">({timezone})</span>
                </label>
                {slotsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
                    <CircleNotch className="w-4 h-4 animate-spin" /> Loading availability...
                  </div>
                ) : slotsError ? (
                  <p className="text-sm text-red-600 dark:text-red-400 py-2">{slotsError}</p>
                ) : slots.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">No open slots on this day. Try another date.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-1.5 max-h-40 overflow-y-auto pr-1">
                    {slots.map(s => (
                      <button key={s.start}
                        onClick={() => setSelectedSlot(s)}
                        className={cn(
                          'h-9 px-2 text-xs rounded-md border text-center transition-all duration-150',
                          selectedSlot?.start === s.start
                            ? 'border-[#5e6ad2] bg-[#5e6ad2]/10 text-[#5e6ad2] dark:border-[#5e6ad2]/60 dark:bg-[#5e6ad2]/[0.15] dark:text-[#a5b4fc] font-medium ring-1 ring-[#5e6ad2]/30'
                            : 'border-border/60 text-foreground hover:border-[#5e6ad2]/40 hover:bg-muted/40 dark:border-white/[0.08] dark:hover:bg-white/[0.03]',
                        )}>
                        {formatSlotTime(s.start, timezone)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">Title</label>
                <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Meeting title" />
              </div>

            </>
          )}
        </div>

        {/* Footer */}
        {!booked && (
          <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-border/60">
            {provider === 'calendly' && !isReschedule ? (
              <Button variant="ghost" onClick={handleSendCalendlyLink} disabled={!activeContact || booking}>
                Send scheduling link instead
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={handleBook} loading={booking} disabled={!canBook || (isReschedule && provider === 'calendly')}>
                {isReschedule ? 'Reschedule' : 'Book meeting'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}
